import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Register() {
    const { registerWithEmail } = useAuth();
    const navigate = useNavigate();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await registerWithEmail(email, password, name);
            toast.success("Welcome. Let's take a breath together.");
            navigate("/onboarding", { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not create your account.");
        } finally {
            setSubmitting(false);
        }
    };

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const handleGoogle = () => {
        const redirectUrl = window.location.origin + "/dashboard";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    return (
        <div className="min-h-screen cream-grain flex flex-col" data-testid="register-page">
            <header className="max-w-6xl w-full mx-auto px-6 py-6">
                <Link to="/" className="flex items-center gap-2.5">
                    <Flame className="w-5 h-5 text-brand-spark animate-flicker" strokeWidth={1.5} />
                    <span className="font-serif text-2xl text-brand-darkPurple tracking-tight">Lantern</span>
                </Link>
            </header>

            <div className="flex-1 flex items-center justify-center px-6 pb-16">
                <div className="w-full max-w-md">
                    <h1 className="font-serif text-4xl text-brand-darkPurple tracking-tight">Begin, gently.</h1>
                    <p className="mt-2 text-brand-charcoal/70">A quiet room where the transition can breathe.</p>

                    <form onSubmit={submit} className="mt-8 space-y-4">
                        <div>
                            <Label htmlFor="name" className="text-brand-darkPurple text-sm">What should she call you?</Label>
                            <Input
                                data-testid="register-name-input"
                                id="name" required
                                value={name} onChange={(e) => setName(e.target.value)}
                                className="mt-1.5 bg-white border-brand-darkPurple/15 focus-visible:ring-brand-gold/50 h-11"
                            />
                        </div>
                        <div>
                            <Label htmlFor="email" className="text-brand-darkPurple text-sm">Email</Label>
                            <Input
                                data-testid="register-email-input"
                                id="email" type="email" required
                                value={email} onChange={(e) => setEmail(e.target.value)}
                                className="mt-1.5 bg-white border-brand-darkPurple/15 focus-visible:ring-brand-gold/50 h-11"
                            />
                        </div>
                        <div>
                            <Label htmlFor="password" className="text-brand-darkPurple text-sm">Password <span className="text-brand-charcoal/50 text-xs">(min. 6 characters)</span></Label>
                            <Input
                                data-testid="register-password-input"
                                id="password" type="password" required minLength={6}
                                value={password} onChange={(e) => setPassword(e.target.value)}
                                className="mt-1.5 bg-white border-brand-darkPurple/15 focus-visible:ring-brand-gold/50 h-11"
                            />
                        </div>
                        <Button
                            data-testid="register-submit-btn"
                            type="submit"
                            disabled={submitting}
                            className="w-full bg-brand-darkPurple hover:bg-brand-darkPurpleSoft text-white rounded-full h-11"
                        >
                            {submitting ? "Lighting…" : "Light the lantern"}
                        </Button>
                    </form>

                    <div className="my-6 flex items-center gap-3 text-xs text-brand-charcoal/50">
                        <div className="flex-1 h-px bg-brand-darkPurple/10" />
                        or
                        <div className="flex-1 h-px bg-brand-darkPurple/10" />
                    </div>

                    <Button
                        data-testid="register-google-btn"
                        onClick={handleGoogle}
                        variant="outline"
                        className="w-full border-brand-darkPurple/20 bg-white hover:bg-brand-cream text-brand-darkPurple rounded-full h-11"
                    >
                        <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Continue with Google
                    </Button>

                    <p className="mt-8 text-center text-sm text-brand-charcoal/70">
                        Already here? <Link to="/login" className="text-brand-darkPurple underline underline-offset-4 hover:text-brand-spark">Sign in</Link>.
                    </p>
                </div>
            </div>
        </div>
    );
}
